import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/ScrollPinSection.css';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface CardData {
  id: string;
  number: string;
  title: string;
  description: string;
  features?: string[];
  bgColor: string;
  image?: string;
}

type ScrollPinSectionProps = {
  sectionHeading : string;
  cardData : CardData[];
}

// const cardData: CardData[] = [
//   {
//     id: 'card1',
//     number: '01',
//     title: 'Control Cloud Costs Without Sacrificing Performance',
//     description: 'Identify and eliminate idle compute, overprovisioned storage, and non-critical workloads. Implement auto-scaling, rightsizing, and spot/preemptible usage to reduce cloud spend.',
//     features: [
//       'Auto-scaling and rightsizing strategies',
//       'Spot/preemptible instance optimization',
//       'Resource utilization monitoring'
//     ],
//     bgColor: 'white',
//     image: '/assets/control-cloud-cost.svg'
//   },
//   {
//     id: 'card2',
//     number: '02',
//     title: 'Usage Analytics & Visibility',
//     description: 'Build dashboards using Prometheus, Grafana, or native billing APIs to map cost to environments, services, and teams. Enable real-time visibility across multi-cloud usage.',
//     features: [
//       'Real-time cost visibility',
//       'Multi-cloud usage tracking',
//       'Team-based cost attribution'
//     ],
//     bgColor: 'white',
//     image: '/assets/usage-analytics.png'
//   },
//   {
//     id: 'card3',
//     number: '03',
//     title: 'Architecture-Aware Cost Efficiency',
//     description: 'Redesign infrastructure for cost awareness: shift from monoliths to autoscaled microservices, optimize networking and storage classes based on traffic and data patterns.',
//     features: [
//       'Network optimization',
//       'Storage class optimization',
//       'Traffic pattern analysis'
//     ],
//     bgColor: 'white',
//     image: '/assets/architecture-aware.png'
//   }
// ];

const ScrollPinSection: React.FC<ScrollPinSectionProps> = ({
  sectionHeading,
  cardData,

}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  // const mainHeadingRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  // const endMarkerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const triggers: ScrollTrigger[] = [];
  
      const section = sectionRef.current;
      // const endMarker = endMarkerRef.current;
      
      if (!section) return;
  
    // Get all cards and headers
      const cards = cardRefs.current.filter(Boolean) as HTMLDivElement[];
      const headers = headerRefs.current.filter(Boolean) as HTMLDivElement[];
  
      function getHeaderHeight() {
        if (window.innerWidth <= 768) return 60; 
        return 70;
      }
      const headerHeight = getHeaderHeight();
  
      function pinHeadersUpTo(index: number) {
        for (let i = 0; i <= index; i++) {
          const topPosition = window.innerWidth <= 768 ? 
            i * headerHeight: 
            i * headerHeight * 1.4;
          
          gsap.set(headers[i], {
            position: "fixed",
            top: topPosition,
            left: "50%",
            xPercent: -50,
            width: "100%",
            maxWidth: "1416px",
            zIndex: 100 - i,
          });
          headers[i].classList.add("pinned");
        }
      }
  
      function unpinHeadersAbove(index: number) {
        for (let i = index + 1; i < headers.length; i++) {
          gsap.set(headers[i], {
            position: "relative",
          top: 0,
          left: 0,
            xPercent: 0,
            width: "auto",
            maxWidth: "none",
          });
          headers[i].classList.remove("pinned");
        }
      }
  
      function unpinAll() {
        for (let i = 0; i < headers.length; i++) {
          gsap.set(headers[i], {
            position: "relative",
          top: 0,
          left: 0,
            xPercent: 0,
            width: "auto",
            maxWidth: "none",
          });
          headers[i].classList.remove("pinned");
        }
      }

      unpinAll();

  
    // Section-wide trigger to unpin all when leaving section
      triggers.push(
        ScrollTrigger.create({
          trigger: section,
          start: "top top",
          end: () => window.innerWidth <= 768 ? 
          `bottom+=${headers.length * headerHeight} bottom` : 
          "bottom top",
          onLeave: () => unpinAll(),
          onEnterBack: () => {
            let lastIndex = -1;
            cards.forEach((card, i) => {
              const rect = card.getBoundingClientRect();
              if (rect.top < window.innerHeight * (window.innerWidth <= 768 ? 0.2 : 0.3)) {
                lastIndex = i;
              }
            });
            if (lastIndex >= 0) {
              pinHeadersUpTo(lastIndex);
              unpinHeadersAbove(lastIndex);
            }
          }
        })
      );
  
      cards.forEach((card, index) => {
        triggers.push(
          ScrollTrigger.create({
            trigger: card,
            start: () => {
              if (index === 0) return "top top";
            
              return window.innerWidth <= 768
                ? "top 30%"
                : "top top";
            },
            end: window.innerWidth <= 768 ? 
            `bottom+=${(headers.length - index - 1) * headerHeight} 30%` : 
            "bottom top",
            onEnter: () => {
              pinHeadersUpTo(index);
              unpinHeadersAbove(index);
            },
            onEnterBack: () => {
              pinHeadersUpTo(index);
              unpinHeadersAbove(index);
            },
            onLeaveBack: () => {
              unpinHeadersAbove(index - 1);
            }
          })
        );
      });
    
    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [cardData]);

  return (
    <div ref={sectionRef} className="scroll-pin-section">
      <div className="scroll-pin-main-heading">
        <h2>{sectionHeading}</h2>
        <div className="scroll-pin-heading-underline"></div>
      </div>

      <div className="scroll-pin-cards-container">
        {cardData.map((card, index) => (
          <div
            key={card.id}
            className="scroll-pin-card"
            style={{ backgroundColor: card.bgColor }}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
          >
            <div
              className="scroll-pin-card-header"
              ref={(el) => {
                headerRefs.current[index] = el;
              }}
            >
              <div className="scroll-pin-card-number">{card.number}</div>
              <h3 className="scroll-pin-card-title">{card.title}</h3>
            </div>

            <div className="scroll-pin-card-content">
              <div className="scroll-pin-card-left">
                <p className="scroll-pin-card-description">{card.description}</p>
                <ul className="scroll-pin-card-features">
                  {card.features?.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <span className="scroll-pin-feature-dash">–</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="scroll-pin-card-right">
                {card.image && (
                  <div className="scroll-pin-card-image">
                    <img src={card.image} alt={card.title} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {/* Invisible element to mark the end of the section */}
        {/* <div className="scroll-pin-section-end" ref={endMarkerRef}></div> */}
      </div>
    </div>
  );
};

export default ScrollPinSection;